import React, { createContext, useContext, useCallback } from 'react';
import { useToast } from './ToastContext';

interface ErrorContextType {
  handleError: (error: unknown, customMessage?: string) => void;
  handleAsyncError: <T>(
    operation: () => Promise<T>,
    customMessage?: string
  ) => Promise<T | null>;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export function ErrorProvider({ children }: { children: React.ReactNode }) {
  const toast = useToast();

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'Une erreur inattendue s\'est produite';
  };

  const handleError = useCallback((error: unknown, customMessage?: string) => {
    console.error('Error handled:', error);
    
    const errorMessage = customMessage || getErrorMessage(error);
    toast.error('Erreur', errorMessage);
  }, [toast]);

  const handleAsyncError = useCallback(async <T,>(
    operation: () => Promise<T>,
    customMessage?: string
  ): Promise<T | null> => {
    try {
      return await operation();
    } catch (error) {
      handleError(error, customMessage);
      return null;
    }
  }, [handleError]);

  return (
    <ErrorContext.Provider
      value={{
        handleError,
        handleAsyncError,
      }}
    >
      {children}
    </ErrorContext.Provider>
  );
}

export function useError() {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
}